import _ from 'lodash';
import FormElement from './FormElement';
import ReadOnlyMixin from './mixins/ReadOnlyMixin';
import mixin from '../privates/utils/mixin';
import DisabledMixin from './mixins/DisabledMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import FocusMixin from './mixins/FocusMixin';
import ClickableMixin from './mixins/ClickableMixin';
import * as members from '../privates/privateMembers';
import {validate} from '../privates/validations/validation';
import {nullIfNil} from '../privates/utils';
import {runValidationsAndSyncAfterChange} from '../privates/validationsService/validationsService';
import RequiredMixin from './mixins/RequiredMixin';

/**
 * @class DatePicker
 * @summary A `DatePicker` is used for entering dates. It lets users populate a
 *  date field by picking a date using a calendar popup.
 * @mixes $w.FormElement
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.DisabledMixin
 * @mixes $w.FocusMixin
 * @mixes $w.ReadOnlyMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.RequiredMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.inputs.DatePicker
 * @definitionId wysiwyg.viewer.components.inputs.DatePicker
 * @tagname datepicker
 */
class DatePicker extends FormElement {

    /**
     * @member value
     * @label property
     * @syntax
     * get value(): Date
     * set value(value: Date): void
     * @summary Sets or gets the value of the date picker.
     * > **Notes:**
     *  > + Changing a date picker's `value` in code does not trigger an `onChange` event.
     * >
     * >
     * > + If a date picker is connected to a dataset, setting the date picker's `value`
     *  in code does not set the value of the connected field in the dataset.
     *  That means if you use the dataset to perform a submit, the value changed
     *  in code is not reflected in the submitted item.
     * >
     * >
     * > + To submit the new value using a dataset, set the field's value using the
     *  [`setFieldValue()`](wix-dataset.html#setFieldValue) function before
     *  performing the submit.
     * @snippet [DatePicker-value_get.es6=Get a datepicker's date value]
     * @snippet [DatePicker-value_set.es6=Set a datepicker's date value to be today]
     * @type {external:Date}
     * @memberof $w.DatePicker
     * @instance
     * @override
     */
    get value() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        return RMI.getState(compId).selectedDate;
    }

  /**
   * @member value
   * @memberof $w.DatePicker
   * @param {external:Date} value
   */
    set value(value) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'value',
            dataToValidate: {value},
            compName: this.id
        });
        if (!_.isNil(value) && !isValid) {
            return;
        }

        const dateValue = nullIfNil(value);

        const newState = _.defaults({selectedDate: dateValue}, RMI.getState(compId));

        runValidationsAndSyncAfterChange(RMI, compId, dateValue);
        RMI.updateModel({[compId]: {state: newState}});
        RMI.executeCompBehavior(compId, 'setValue', {value: dateValue});
    }

    /**
     * @member maxDate
     * @label property
     * @syntax
     * get maxDate(): Date
     * set maxDate(value: Date): void
     * @summary Sets or gets the latest date that a user can select.
     * @description
     * Setting the `maxDate` property prevents users from selecting
     * dates past the maximum date. However, it does not limit your ability to
     * change the date picker's value to one that exceeds `maxDate`
     * programmatically.
     *
     * Set `maxDate` to `undefined` to remove the
     * current maximum date limitation.
     *
     * Getting the `maxDate` property returns the current maximum
     * date that a user can select.
     * @snippet [DatePicker-maxDate_get.es6=Get a date picker's maximum date]
     * @snippet [DatePicker-maxDate_set.es6=Set a date picker's maximum date to today]
     * @type {external:Date}
     * @default undefined
     * @see [minDate](#minDate)
     * @memberof $w.DatePicker
     * @instance
     */
    get maxDate() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const maxDateISO = RMI.getData(compId).maxDate;

        return maxDateISO && new Date(maxDateISO);
    }

  /**
   * @member maxDate
   * @memberof $w.DatePicker
   * @param {external:Date} maxDate
   */
    set maxDate(maxDate) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'maxDate',
            dataToValidate: {maxDate},
            compName: this.id
        });
        if (!_.isNil(maxDate) && !isValid) {
            return;
        }
        const maxDateToSet = nullIfNil(maxDate) ? maxDate.toISOString() : null;
        RMI.setData(compId, {maxDate: maxDateToSet});
    }

    /**
     * @member minDate
     * @label property
     * @syntax
     * get minDate(): Date
     * set minDate(value: Date): void
     * @summary Sets or gets the earliest date that a user can select.
     * @description
     * Setting the `minDate` property prevents users from selecting
     * dates before the minimum date. However, it does not limit your ability to
     * change the date picker's value to one that precedes `minDate`
     * programmatically.
     *
     * Set `minDate` to `undefined` to remove the current minimum date limitation.
     *
     * Getting the `minDate` property returns the current minimum
     * date that a user can select.
     * @snippet [DatePicker-minDate_get.es6=Get a date picker's minimum date]
     * @snippet [DatePicker-minDate_set.es6=Set a date picker's minimum date to today]
     * @type {external:Date}
     * @default undefined
     * @see [maxDate](#maxDate)
     * @memberof $w.DatePicker
     * @instance
     */
    get minDate() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const minDateISO = RMI.getData(compId).minDate;

        return minDateISO && new Date(minDateISO);
    }

  /**
   * @member minDate
   * @memberof $w.DatePicker
   * @param {external:Date} minDate
   */
    set minDate(minDate) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'minDate',
            dataToValidate: {minDate},
            compName: this.id
        });
        if (!_.isNil(minDate) && !isValid) {
            return;
        }
        const minDateToSet = nullIfNil(minDate) ? minDate.toISOString() : null;
        RMI.setData(compId, {minDate: minDateToSet});
    }

    /**
     * @member disabledDates
     * @label property
     * @syntax
     * get disabledDates(): Date[]
     * set disabledDates(value: Date[]): void
     * @summary Sets or gets the dates that a user cannot select.
     * @description
     *  Setting the `disabledDates` property prevents users from
     *  selecting the specified dates. However, it does not limit your ability
     *  to set those dates programmatically.
     *
     *  Set `disabledDates` to an empty array (`[]`) to remove the current disabled dates.
     *
     *  Getting the `disabledDates` property returns the current
     *  list of disabled dates.
     * @snippet [DatePicker-disabledDates_get.es6=Get dates which are disabled]
     * @snippet [DatePicker-disabledDates_set.es6=Set dates which are disabled]
     * @type {external:Date[]}
     * @see [disabledDaysOfWeek](#disabledDaysOfWeek)
     * @memberof $w.DatePicker
     * @instance
     */
    get disabledDates() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const disabledDates = RMI.getData(compId).disabledDates;

        return disabledDates && _.map(disabledDates, (date) => new Date(date));
    }

  /**
   * @member disabledDates
   * @memberof $w.DatePicker
   * @param {external:Date[]} disabledDates
   */
    set disabledDates(disabledDates) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        const isArrayValid = validate({
            compType,
            functionName: 'disabledDates',
            dataToValidate: {disabledDates},
            compName: this.id
        });
        if (!_.isNil(disabledDates) && !isArrayValid) {
            return;
        }
        const arrItemsArrValid = _.every(disabledDates, (v, i) => {
            return validate({
                compType,
                functionName: 'disabledDates',
                dataToValidate: {'disableDate': v},
                itemIndex: i,
                compName: this.id
            });
        });
        if (!arrItemsArrValid) {
            return;
        }
        const disabledDateToSet = _.isEmpty(disabledDates) ? [] : _.map(disabledDates, (date) => date.toISOString());
        RMI.setData(compId, {disabledDates: disabledDateToSet});
    }

    /**
     * @member disabledDaysOfWeek
     * @label property
     * @syntax
     * get disabledDaysOfWeek(): Number[]
     * set disabledDaysOfWeek(value: Number[]): void
     * @summary Sets or gets the days of the week that a user cannot select.
     * @description
     *  Setting the `disabledDaysOfWeek` property prevents users
     *  from selecting the specified days of the weeks. However, it does not
     *  limit your ability to set those dates programmatically. Set
     *  `disabledDaysOfWeek` to an empty array (`[]`) to remove the current
     *  disabled days of the week.
     *
     *  Getting the `disabledDaysOfWeek` property returns the
     *  current list of disabled days of the week.
     *
     *  The `disabledDaysOfWeek` property accepts an array of integers from 0 to
     *  6, where 0 represent Sunday, 1 is Monday, 2 is Tuesday, and so on.
     * @snippet [DatePicker-disabledDaysOfWeek_get.es6=Get which days of the week are disabled]
     * @snippet [DatePicker-disabledDaysOfWeek_set.es6=Set Sundays and Saturdays to be disabled]
     * @type {external:Number[]}
     * @see [disabledDates](#disabledDates)
     * @memberof $w.DatePicker
     * @instance
     */
    get disabledDaysOfWeek() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        return RMI.getData(compId).disabledDaysOfWeek;
    }

  /**
   * @member disabledDaysOfWeek
   * @memberof $w.DatePicker
   * @param {external:Number[]} disabledDaysOfWeek
   */
    set disabledDaysOfWeek(disabledDaysOfWeek) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        const isArrayValid = validate({
            compType,
            functionName: 'disabledDaysOfWeek',
            dataToValidate: {disabledDaysOfWeek},
            compName: this.id
        });
        if (!_.isNil(disabledDaysOfWeek) && !isArrayValid) {
            return this;
        }
        const arrItemsValid = _.every(disabledDaysOfWeek, (v, i) => {
            return validate({
                compType,
                functionName: 'disabledDaysOfWeek',
                dataToValidate: {'disableDayOfWeek': v},
                itemIndex: i,
                compName: this.id
            });
        });
        if (!arrItemsValid) {
            return;
        }
        const disabledDateToSet = _.isEmpty(disabledDaysOfWeek) ? [] : disabledDaysOfWeek;
        RMI.setData(compId, {disabledDaysOfWeek: disabledDateToSet});
    }

    toJSON() {
        const {id, type, global, rendered, readOnly, required, value, maxDate, minDate, disabledDates, disabledDaysOfWeek} = this;
        return {
            id,
            type,
            global,
            rendered,
            readOnly,
            required,
            value,
            maxDate,
            minDate,
            disabledDates,
            disabledDaysOfWeek
        };
    }
}

mixin(DatePicker.prototype, HiddenCollapsedMixin.prototype);
mixin(DatePicker.prototype, DisabledMixin.prototype);
mixin(DatePicker.prototype, FocusMixin.prototype);
mixin(DatePicker.prototype, ReadOnlyMixin.prototype);
mixin(DatePicker.prototype, ClickableMixin.prototype);
mixin(DatePicker.prototype, RequiredMixin.prototype);

export default DatePicker;
